import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Download,
  Loader2,
  Check,
  Images,
  FolderDown,
  Share2,
  AlertCircle,
  ExternalLink,
  Smartphone,
  Monitor,
  Hand,
} from 'lucide-react';
import {
  SaveableImage,
  PreparedImage,
  prepareMediaFiles,
  canShareFiles,
  shareFiles,
  downloadFile,
  downloadFilesSequentially,
  openImageInNewTab,
  isIOS,
  isAndroid,
} from '@/lib/mediaSave';

export interface SaveScope {
  key: string;
  label: string;
  images: SaveableImage[];
}

interface SaveImagesModalProps {
  open: boolean;
  onClose: () => void;
  scopes: SaveScope[];
  /** Optional: preselect a single image id (per-post "Save" buttons). */
  focusId?: string | null;
}

type Notice = { tone: 'ok' | 'warn'; text: string } | null;

const SaveImagesModal: React.FC<SaveImagesModalProps> = ({ open, onClose, scopes, focusId }) => {
  const ios = useMemo(() => isIOS(), []);
  const android = useMemo(() => isAndroid(), []);
  const [scopeKey, setScopeKey] = useState(scopes[0]?.key || '');
  const [prepared, setPrepared] = useState<PreparedImage[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const cache = useRef<Map<string, PreparedImage>>(new Map());
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [themeCls, setThemeCls] = useState('');

  const scope = scopes.find((s) => s.key === scopeKey) || scopes[0];
  const images = scope?.images || [];

  // Reset scope when reopened
  useEffect(() => {
    if (open) {
      setScopeKey(scopes[0]?.key || '');
      setNotice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pre-fetch files so iOS share sheet can open synchronously on tap.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const todo = images.filter((i) => !cache.current.has(i.id + i.url));
    const finish = () => {
      if (cancelled) return;
      const list = images.map((i) => cache.current.get(i.id + i.url)!).filter(Boolean);
      setPrepared(list);
      const ok = list.filter((p) => p.file).map((p) => p.id);
      if (focusId && ok.includes(focusId)) setSelected(new Set([focusId]));
      else setSelected(new Set(ok));
    };
    if (todo.length === 0) {
      setPreparing(false);
      finish();
      return;
    }
    setPrepared([]);
    setPreparing(true);
    prepareMediaFiles(todo).then((res) => {
      res.forEach((r) => cache.current.set(r.id + r.url, r));
      if (!cancelled) setPreparing(false);
      finish();
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeKey, images.map((i) => i.id + i.url).join('|'), focusId]);

  // Lock body scroll while open + close on Escape
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Carry the surrounding theme (e.g. .mtn-mono / .theme-romania) into the
  // portal so the panel keeps the same look once rendered at <body> level.
  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const el = anchorRef.current;
    const cls: string[] = [];
    if (el.closest('.mtn-mono')) cls.push('mtn-mono');
    if (el.closest('.theme-romania')) cls.push('theme-romania');
    setThemeCls(cls.join(' '));
  }, [open]);

  if (!open) return <span ref={anchorRef} hidden />;

  const selectedFiles = prepared.filter((p) => selected.has(p.id) && p.file).map((p) => p.file!) as File[];
  const readyCount = prepared.filter((p) => p.file).length;
  const shareSupported = canShareFiles(selectedFiles.length ? selectedFiles : prepared.filter((p) => p.file).slice(0, 1).map((p) => p.file!));

  const flash = (n: Notice) => {
    setNotice(n);
    if (n) setTimeout(() => setNotice((cur) => (cur === n ? null : cur)), 6000);
  };

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allSelected = readyCount > 0 && selectedFiles.length === readyCount;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(prepared.filter((p) => p.file).map((p) => p.id)));

  // ---- actions (called directly from taps — no awaits before share) ----
  const handleShare = (files: File[], key: string, target: 'photos' | 'files' | 'share') => {
    if (!files.length) return flash({ tone: 'warn', text: 'Select at least one image first.' });
    setBusy(key);
    shareFiles(files, scope?.label).then((r) => {
      setBusy(null);
      if (r === 'shared')
        flash({
          tone: 'ok',
          text:
            target === 'photos'
              ? `Done — if you tapped "Save Image", ${files.length > 1 ? 'they are' : 'it is'} now in your Photos app.`
              : target === 'files'
              ? 'Done — if you tapped "Save to Files", find it in the Files app.'
              : 'Shared successfully.',
        });
      else if (r === 'unsupported' || r === 'failed') {
        // Fallback: direct download
        downloadFilesSequentially(files);
        flash({
          tone: 'warn',
          text: ios
            ? 'Share menu unavailable in this browser — images were downloaded to Files > Downloads. Tip: press and hold an image below and tap "Save to Photos".'
            : 'Share menu unavailable — images were downloaded instead.',
        });
      }
    });
  };

  const handleDownload = (files: File[], key: string) => {
    if (!files.length) return flash({ tone: 'warn', text: 'Select at least one image first.' });
    setBusy(key);
    downloadFilesSequentially(files).then(() => {
      setBusy(null);
      flash({
        tone: 'ok',
        text: ios
          ? `Downloaded ${files.length} image${files.length > 1 ? 's' : ''} — open the Files app > Downloads. Tap "Download" if Safari asks.`
          : android
          ? `Downloaded ${files.length} image${files.length > 1 ? 's' : ''} to your Downloads / Gallery.`
          : `Downloaded ${files.length} image${files.length > 1 ? 's' : ''} to your Downloads folder.`,
      });
    });
  };

  const btnPrimary =
    'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 sm:py-3 bg-[#0D1B2A] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#1A3B5C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
  const btnGold =
    'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 sm:py-3 bg-[#C9A961] text-[#0D1B2A] font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#b89651] disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
  const btnGhost =
    'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 sm:py-3 border border-[#1A3B5C]/30 text-[#1A3B5C] font-sans text-[11px] tracking-[0.2em] uppercase hover:border-[#C9A961] hover:text-[#8B6F47] disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  const n = selectedFiles.length;
  const spin = (k: string) => busy === k;

  const modal = (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#0D1B2A]/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[88vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-7 pt-5 pb-4 border-b border-slate-100">
          <div>
            <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-1 flex items-center gap-2">
              {ios || android ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
              {ios ? 'iPhone / iPad detected' : android ? 'Android detected' : 'Desktop'}
            </div>
            <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Save Post Images</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-slate-500 hover:text-[#0D1B2A]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scope + select all */}
        <div className="px-5 sm:px-7 py-3 flex flex-wrap items-center justify-between gap-3 bg-[#F3EFE6]/60">
          {scopes.length > 1 ? (
            <div className="inline-flex border border-[#1A3B5C]/20 bg-white">
              {scopes.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setScopeKey(s.key)}
                  className={`px-3 py-2 font-sans text-[10px] tracking-[0.15em] uppercase transition-colors ${
                    s.key === scope?.key ? 'bg-[#1A3B5C] text-white' : 'text-[#1A3B5C] hover:bg-[#F3EFE6]'
                  }`}
                >
                  {s.label} ({s.images.length})
                </button>
              ))}
            </div>
          ) : (
            <span className="font-sans text-xs text-[#1A3B5C]">{scope?.label}</span>
          )}
          {readyCount > 0 && (
            <button onClick={toggleAll} className="font-sans text-[11px] text-[#1A3B5C] hover:text-[#8B6F47] underline underline-offset-4">
              {allSelected ? 'Deselect all' : `Select all (${readyCount})`}
            </button>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
          {images.length === 0 ? (
            <div className="text-center py-12">
              <Images className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="font-sans text-sm text-slate-500">No images in this group yet. Generate or upload images in Step 3.</p>
            </div>
          ) : preparing && prepared.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3 text-[#1A3B5C]">
              <Loader2 className="w-7 h-7 animate-spin text-[#C9A961]" />
              <span className="font-sans text-sm">Preparing {images.length} image{images.length > 1 ? 's' : ''} for saving…</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {prepared.map((p) => {
                const isSel = selected.has(p.id);
                return (
                  <div key={p.id} className={`group border ${isSel ? 'border-[#C9A961] ring-2 ring-[#C9A961]/40' : 'border-slate-200'} bg-white`}>
                    <div className="relative aspect-square bg-slate-100">
                      {/* Real <img> so iOS long-press "Save to Photos" works */}
                      <img
                        src={p.url}
                        alt={p.label}
                        className="w-full h-full object-cover select-none"
                        style={{ WebkitTouchCallout: 'default' } as React.CSSProperties}
                      />
                      <button
                        onClick={() => p.file && toggle(p.id)}
                        disabled={!p.file}
                        aria-label={isSel ? 'Deselect image' : 'Select image'}
                        className={`absolute top-2 left-2 w-7 h-7 rounded-full border-2 flex items-center justify-center shadow ${
                          isSel ? 'bg-[#C9A961] border-[#C9A961] text-[#0D1B2A]' : 'bg-white/90 border-white text-transparent'
                        }`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      {p.error && (
                        <div className="absolute inset-x-0 bottom-0 bg-rose-600/90 text-white text-[10px] font-sans px-2 py-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Couldn't prepare — use Open
                        </div>
                      )}
                    </div>
                    <div className="px-2.5 py-2">
                      <div className="font-sans text-[12px] font-semibold text-[#1A3B5C] truncate">{p.label}</div>
                      {p.sublabel && <div className="font-sans text-[10px] text-slate-500 truncate">{p.sublabel}</div>}
                      <div className="flex items-center gap-1.5 mt-2">
                        {p.file ? (
                          ios ? (
                            <>
                              <button
                                onClick={() => handleShare([p.file!], `one-photos-${p.id}`, 'photos')}
                                className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-[#0D1B2A] text-white text-[10px] font-sans uppercase tracking-wider"
                              >
                                <Images className="w-3 h-3" /> Photos
                              </button>
                              <button
                                onClick={() => downloadFile(p.file!)}
                                className="flex-1 inline-flex items-center justify-center gap-1 py-2 border border-[#1A3B5C]/30 text-[#1A3B5C] text-[10px] font-sans uppercase tracking-wider"
                              >
                                <FolderDown className="w-3 h-3" /> Files
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => downloadFile(p.file!)}
                              className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-[#0D1B2A] text-white text-[10px] font-sans uppercase tracking-wider hover:bg-[#1A3B5C]"
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                          )
                        ) : (
                          <button
                            onClick={() => openImageInNewTab(p.url)}
                            className="flex-1 inline-flex items-center justify-center gap-1 py-2 border border-[#1A3B5C]/30 text-[#1A3B5C] text-[10px] font-sans uppercase tracking-wider"
                          >
                            <ExternalLink className="w-3 h-3" /> Open
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Notice */}
        {notice && (
          <div
            className={`mx-5 sm:mx-7 mb-3 px-4 py-3 text-[12px] font-sans flex items-start gap-2 ${
              notice.tone === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}
          >
            {notice.tone === 'ok' ? <Check className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span>{notice.text}</span>
          </div>
        )}

        {/* Footer actions */}
        <div className="border-t border-slate-100 px-5 sm:px-7 py-4 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          {ios ? (
            <>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  disabled={!n || preparing}
                  onClick={() => handleShare(selectedFiles, 'all-photos', 'photos')}
                  className={btnGold}
                >
                  {spin('all-photos') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Images className="w-4 h-4" />}
                  Save {n > 1 ? `${n} Images` : 'Image'} to Camera Roll
                </button>
                <button
                  disabled={!n || preparing}
                  onClick={() => (shareSupported ? handleShare(selectedFiles, 'all-files', 'files') : handleDownload(selectedFiles, 'all-files'))}
                  className={btnPrimary}
                >
                  {spin('all-files') ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderDown className="w-4 h-4" />}
                  Save to Files
                </button>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2 font-sans text-[11px] text-slate-600 leading-relaxed">
                <p className="flex gap-2"><Images className="w-3.5 h-3.5 text-[#C9A961] shrink-0 mt-0.5" /> <span><b>Camera Roll:</b> in the menu that opens, tap <b>{n > 1 ? `"Save ${n} Images"` : '"Save Image"'}</b>.</span></p>
                <p className="flex gap-2"><FolderDown className="w-3.5 h-3.5 text-[#C9A961] shrink-0 mt-0.5" /> <span><b>Files:</b> tap <b>"Save to Files"</b> in the menu and pick a folder (iCloud Drive or On My iPhone).</span></p>
                <p className="flex gap-2 sm:col-span-2"><Hand className="w-3.5 h-3.5 text-[#C9A961] shrink-0 mt-0.5" /> <span>Tip: you can also press and hold any image above and tap <b>"Save to Photos"</b>.</span></p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                {android && shareSupported && (
                  <button disabled={!n || preparing} onClick={() => handleShare(selectedFiles, 'all-share', 'share')} className={btnGhost}>
                    {spin('all-share') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Share / Save to Gallery
                  </button>
                )}
                <button disabled={!n || preparing} onClick={() => handleDownload(selectedFiles, 'all-dl')} className={btnGold}>
                  {spin('all-dl') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download {n > 1 ? `${n} Images` : n === 1 ? 'Image' : 'Selected'}
                </button>
              </div>
              <p className="mt-3 font-sans text-[11px] text-slate-500">
                {android
                  ? 'Downloads appear in your Downloads folder and Gallery app.'
                  : 'Images save to your Downloads folder. If your browser asks, allow multiple downloads.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Render at <body> level so parent containers with overflow-hidden /
  // backdrop-filter / transforms can't trap or clip the fixed overlay.
  return (
    <>
      <span ref={anchorRef} hidden />
      {typeof document !== 'undefined' ? createPortal(<div className={themeCls}>{modal}</div>, document.body) : modal}
    </>
  );
};

export default SaveImagesModal;
